import React from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import FormButton from "./FormButton";
import { useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import LoopIcon from "@mui/icons-material/Loop";
import { string, object, func, node, number, bool } from "prop-types";

const Form = ({
  title = "",
  onSubmit,
  onReset,
  hasFormErrors,
  to = "/",
  color = "inherit",
  spacing = 1,
  styles,
  children,
  showReset = true,
  // #50. A form whose submit fires a network write has to be able to say so:
  // without this the Submit control stays live for the whole request and every
  // impatient extra tap is another write. Optional and false by default, so
  // every form that does not pass it renders exactly as it did before.
  submitting = false,
}) => {
  const navigate = useNavigate();

  /**
   * Every submission now arrives here, from the form's own submit event.
   *
   * The Submit control below is a real `type="submit"` button rather than an
   * onClick handler, which is what makes Enter (and the Android IME's Go key)
   * work: with no submitter and more than one field that blocks implicit
   * submission, the browser is required by spec to do nothing at all — so the
   * signup form answered Enter with silence while the sign-in page one tap
   * away, which hand-rolls its own `<button type="submit">`, worked.
   *
   * preventDefault is called here rather than trusted to the consumer: the
   * one live consumer does it first thing, but an omission would be a real
   * page navigation that no test can see — jsdom answers an uncancelled
   * submit with a virtual-console message, not a failure.
   */
  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    // Belt and braces for a PROGRAMMATIC submit (`form.requestSubmit()` or
    // `form.submit()`), which bypasses the button entirely. Nothing in this app
    // does that today; the guard is here so a future caller cannot reopen #50
    // by reaching the form directly.
    //
    // It is deliberately NOT justified by Enter or the Android IME's Go key.
    // #50 first claimed those bypassed a disabled Submit; a skeptic measured
    // the opposite in real Chrome 151 over CDP — implicit submission activates
    // the DEFAULT BUTTON, so with Submit disabled Enter produced 0 submits and
    // 0 clicks, while the same keystroke with the button enabled produced 1 of
    // each. `disabled` alone already stops the keyboard path. That also agrees
    // with the #196 note above rather than contradicting it: Enter routes
    // through the submitter, which is exactly why a form with none did nothing.
    if (submitting) return;
    onSubmit(e);
  };
  return (
    <Box
      component="form"
      color={color}
      sx={{ mt: 2, p: { xs: 1, sm: 2 }, ...styles }}
      onSubmit={handleSubmit}
      autoComplete="off"
      noValidate
    >
      <Typography align="center" variant="h5" component="h1" mb={2}>
        {title.toUpperCase()}
      </Typography>

      <Grid container spacing={spacing}>
        {children}
      </Grid>

      <Grid container spacing={1} my={2} direction="row" width="100">
        <Grid item xs={12} sm={showReset ? 6 : 12}>
          <FormButton
            node="cancel"
            color="error"
            component="div"
            variant="outlined"
            onClick={() => navigate(to)}
            size="medium"
          />
        </Grid>
        {/* The reset/refresh control is opt-OUT: `showReset` defaults to true (see the
            prop default above and defaultProps below), so a screen that does not want
            it must pass `showReset={false}`. This comment said "opt-in" for long enough
            that it is worth naming the risk: a sprint reading it would conclude a form
            without the prop has no reset control, and every such form has one.
            The rest of this comment used to say some screens (e.g. Settings,
            #151) deliberately hide it. NO SCREEN DOES. `grep -rn showReset
            react-app/src` finds the prop only here, in propTypes/defaultProps
            and in this component's own test — no caller anywhere passes
            `showReset={false}`, and the settings screens do not render <Form>
            at all (they use users/components/settings/SettingsGroup). This
            component's only importer is users/components/UserForm.jsx and its
            only consumer is users/pages/SignUpPage.jsx, so signup is the sole
            place the control ships. #151 recorded the JUDGEMENT — that a single
            "revert everything" button beside a profile form reads as a
            mysterious data-loss trap, not a feature — but it was never applied
            in code. Hiding it on signup ([#49]) would therefore be the FIRST
            such decision, not the application of an existing one. */}
        {showReset && (
          <Grid item xs={12} sm={6}>
            <FormButton
              node={<LoopIcon />} ariaLabel="Clear form" /* B-24: icon only */
              variant="outlined"
              component="div"
              onClick={onReset}
              size="medium"
              color="primary"
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <FormButton
            node="Submit"
            type="submit"
            component="button"
            size="large"
            variant="contained"
            color="primary"
            disabled={submitting}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
Form.propTypes = {
  children: node.isRequired,
  onSubmit: func.isRequired,
  color: string,
  to: string,
  spacing: number,
  onReset: func,
  hasFormErrors: func.isRequired,
  title: string,
  styles: object,
  showReset: bool,
  submitting: bool,
};
Form.defaultProps = {
  color: "inherit",
  to: "/",
  spacing: 1,
  title: "",
  styles: {},
  showReset: true,
  submitting: false,
};
export default React.memo(Form);
